import { Separator } from "@/components/ui/separator.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";

import useMediaAttachment from "@/hooks/useMediaAttachment";
import { generateImageName } from "@/lib/utils";
import * as AF from "../form-items/AbstractForm";

interface HomepageFormProps {
  language: SupportedLocale;
}

const HomepageForm = ({ language }: HomepageFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const imagePosition = useMemo(
    () => `homepage${language.toLocaleUpperCase()}`,
    [language],
  );

  const { attachment, previewImageUrl, refetch } = useMediaAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <CroppedImageInput
        docId={instanceData?.id}
        imageURL={previewImageUrl}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={0.78}
        label={t("homepageArtistPortraitLabel")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.homepage"),
          })
        }
      />

      <AF.Text
        name={`${language}.websiteTitle`}
        label={t("cms.homepage.form.websiteTitle")}
      />
      <AF.Text
        name={`${language}.websiteSubtitle`}
        label={t("cms.homepage.form.websiteSubtitle")}
      />
      {instanceData?.hideCr !== true && (
        <AF.Text
          name={`${language}.catalogueRaisonneCTA`}
          label={t("cms.homepage.form.catalogueRaisonneCTA")}
        />
      )}
      <Separator />
      <AF.Text
        name={`${language}.artistName`}
        label={t("cms.homepage.form.artistName")}
        tootltip={t("cms.homepage.form.artistName.tooltip")}
      />
      <Separator />
      <AF.Text
        name={`${language}.birthDate`}
        label={t("cms.homepage.form.birthDate")}
      />
      <AF.Text
        name={`${language}.deathDate`}
        label={t("cms.homepage.form.deathDate")}
      />
      <AF.Text
        name={`${language}.credits`}
        label={t("cms.homepage.form.credits")}
      />
      <Separator />
      <AF.RichText
        name={`${language}.resume`}
        label={t("cms.homepage.form.resume")}
      />
      <Separator />
      <AF.Text
        name={`${language}.footerArchives`}
        label={t("cms.homepage.form.footerArchives")}
      />
    </AF.Col>
  );
};

export default HomepageForm;
